import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcut utilities for Edit page.
 * Extracted to reduce cognitive complexity in Edit.jsx (S3776).
 *
 * Uses lookup tables to minimize if/else chains.
 */

/**
 * Check if keyboard event should be ignored (typing in input, modal open, etc.)
 */
export function shouldIgnoreKeyEvent(event, state) {
  const { showPersonModal, showIncompleteModal, savingPhoto, showFullRes } = state;

  // Close full-res viewer on Escape (handled separately, don't ignore)
  if (event.key === 'Escape' && showFullRes) return false;

  // Ignore when modal open, saving, or typing in input
  if (showPersonModal || showIncompleteModal || savingPhoto) return true;

  const activeTag = document.activeElement?.tagName?.toUpperCase();
  return activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';
}

/**
 * Build key identifier for lookup (handles modifiers)
 */
function getKeyId(e) {
  const parts = [];
  if (e.metaKey) parts.push('meta');
  if (e.ctrlKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(e.key.toLowerCase());
  return parts.join('+');
}

/**
 * Execute action and prevent default
 */
function executeAction(e, action) {
  e.preventDefault();
  action();
  return true;
}

/**
 * Handle global keyboard shortcuts using lookup table
 */
export function handleGlobalShortcuts(e, state, actions) {
  const { showFullRes, adjustments } = state;

  // Build lookup table for global shortcuts
  const shortcuts = {
    'tab': () => actions.toggleSidebars(),
    'meta+shift+c': () => actions.handleCopyAdjustments(),
    'meta+shift+v': () => actions.handlePasteAdjustments(),
    'alt+v': () => actions.handleCycleVizLayers(),
    'shift+enter': () => actions.handleMarkDone(),
    'd': () => actions.handleMarkDone(),
    '7': () => actions.handleSkip(),
  };

  // Special case: Escape only when fullRes is shown
  if (e.key === 'Escape' && showFullRes) {
    return executeAction(e, () => actions.setShowFullRes(false));
  }

  // Special case: Backslash only when adjustments are non-default
  if (e.key === '\\' && !actions.isDefaultAdjustments(adjustments)) {
    return executeAction(e, () => actions.setShowOriginal(true));
  }

  // Lookup and execute
  const keyId = getKeyId(e);
  const action = shortcuts[keyId];
  if (action) return executeAction(e, action);

  return false;
}

/**
 * Handle crop tilt adjustment
 */
function handleTilt(e, actions) {
  const tiltStep = e.ctrlKey ? 0.5 : 0.1;
  const delta = e.key === 'ArrowLeft' ? -tiltStep : tiltStep;
  actions.setRotationByUser((r) => r + delta);
}

/**
 * Handle crop pan with arrow keys (zoom/pan model)
 */
function handleCropPan(e, actions) {
  const delta = { x: 0, y: 0 };

  if (e.key === 'ArrowLeft') delta.x = -1;
  else if (e.key === 'ArrowRight') delta.x = 1;
  else if (e.key === 'ArrowUp') delta.y = -1;
  else if (e.key === 'ArrowDown') delta.y = 1;

  actions.nudgePan(delta);
}

/**
 * Handle crop mode specific shortcuts
 */
export function handleCropModeShortcuts(e, state, actions) {
  // Escape exits crop mode (discard uncommitted changes)
  if (e.key === 'Escape') {
    return executeAction(e, () => actions.exitCropMode());
  }

  // Simple key shortcuts (lookup table)
  const shortcuts = {
    's': () => actions.handleRecropConfirm(),
    'r': () => actions.handleResetCrop(),
    'q': () => actions.setFullFrameCrop(),
    'enter': () => actions.handleRecropConfirm(),
    'x': () => actions.flipCropOrientation(),
  };

  const keyLower = e.key.toLowerCase();
  const action = shortcuts[keyLower];
  if (action && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
    return executeAction(e, action);
  }

  // Enter without shift
  if (e.key === 'Enter' && !e.shiftKey) {
    return executeAction(e, () => actions.handleRecropConfirm());
  }

  // Arrow key handling for tilt (shift+arrow)
  if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
    return executeAction(e, () => handleTilt(e, actions));
  }

  // Arrow key handling for pan (zoom/pan model)
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
    return executeAction(e, () => handleCropPan(e, actions));
  }

  return false;
}

/**
 * Handle navigation mode shortcuts
 */
export function handleNavigationShortcuts(e, state, actions) {
  const { selectedImage } = state;

  if (e.key === 's') {
    e.preventDefault();
    actions.enterCropMode();
    return true;
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault();
    const prevId = actions.getPrevImageId();
    if (prevId) actions.setSelectedImage(prevId);
    return true;
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    const nextId = actions.getNextImageId();
    if (nextId && nextId !== selectedImage) actions.setSelectedImage(nextId);
    return true;
  }

  return false;
}

/**
 * Custom hook for Edit page keyboard shortcuts
 */
export function useEditKeyboardShortcuts({ enabled = true, cropMode, state, actions }) {
  const handleKeyDown = useCallback(
    (e) => {
      if (!enabled || shouldIgnoreKeyEvent(e, state)) return;
      if (handleGlobalShortcuts(e, state, actions)) return;
      if (cropMode) {
        handleCropModeShortcuts(e, state, actions);
      } else {
        handleNavigationShortcuts(e, state, actions);
      }
    },
    [enabled, cropMode, state, actions]
  );

  useEffect(() => {
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Hook for keyup events (backslash release for before/after)
 */
export function useEditKeyUp(onBackslashRelease) {
  useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.key === '\\') onBackslashRelease();
    };
    globalThis.addEventListener('keyup', handleKeyUp);
    return () => globalThis.removeEventListener('keyup', handleKeyUp);
  }, [onBackslashRelease]);
}

export default useEditKeyboardShortcuts;
